import { PROOFS, SeedEnvelopeSchema, ClosedRunSchema, ObservationSchema, type Continuity, type SeedEnvelope, type ClosedRun, type Observation } from '@dissidium/contracts';
import { hash, canonical } from '../kernel/primitives.ts';
import { validateLedger, nextRunOrdinal, promote, bequeath, settlementAccount, assertCompleteAccount, assertContinuityTransition } from './ledger.ts';
import { RETURN_DATE, FIRST_EMBER } from './calendar.ts';

export function closeRun(c: Continuity, input: ClosedRun, proofs: Observation[], bequestId: string | null = null): Continuity {
  let next = validateLedger(c);
  const run = ClosedRunSchema.parse(input);
  if (run.ordinal !== nextRunOrdinal(c) || run.chronologyId !== c.chronologyId || run.namespace !== c.namespace) throw new Error('Run closure is out of order or belongs to another chronology');
  const pc = next.persons[run.pcId];
  if (!pc || (pc.lifecycle === 'dead') !== (run.outcome === 'died')) throw new Error('Run outcome must match the actual PC fate');
  if (bequestId) next = bequeath(next, bequestId, run.pcId);
  if (run.outcome === 'survived') next = promote(next, run.pcId, 'legacy');
  for (const inputProof of proofs) {
    const proof = ObservationSchema.parse(inputProof);
    if (proof.runId !== run.id || !run.actions.some(a => a.id === proof.actionId && a.proofId === proof.proofId && a.routeId === proof.routeId)) throw new Error('Observation lacks its actual source verification action');
    next.observations.push(proof);
  }
  next.closedRuns.push(run);
  next.date = { ...RETURN_DATE };
  return validateLedger(next);
}
export function settlementEligibility(input: SeedEnvelope, frozen: Continuity): { eligible: boolean; reasons: string[] } {
  const envelope = SeedEnvelopeSchema.parse(input);
  const c = validateLedger(frozen);
  const reasons: string[] = [];
  if (envelope.continuityDigest !== hash(c) || envelope.chronologyId !== c.chronologyId) reasons.push('The opening must retain its exact continuity checkpoint.');
  if (envelope.namespace !== 'production' || c.namespace !== 'production' || envelope.rulesetId !== 'production-campaign') reasons.push('Bench, prototype, demo and debug histories cannot unlock production settlement.');
  const ordered = [...c.closedRuns].sort((a, b) => a.ordinal - b.ordinal);
  if (ordered.some((r, i) => r.ordinal !== i + 1) || envelope.runOrdinal !== ordered.length + 1 || envelope.runOrdinal < 4) reasons.push('The opportunity opens only after at least three consecutive closed runs.');
  if (c.settlement === 'settled') reasons.push('This chronology is already settled.');
  const verified = new Set<string>();
  const sourceRuns = new Set<string>();
  const actionKeys = new Set<string>();
  for (const observation of c.observations) {
    const r = ordered.find(run => run.id === observation.runId);
    const action = r?.actions.find(a => a.id === observation.actionId && a.proofId === observation.proofId && a.routeId === observation.routeId);
    const actionKey = `${observation.runId}/${observation.actionId}`;
    if (!r || !action || r.namespace !== 'production' || r.rulesetId !== 'production-campaign' || r.chronologyId !== c.chronologyId || r.ordinal >= envelope.runOrdinal || actionKeys.has(actionKey)) {
      reasons.push(`Observation ${observation.id} lacks distinct, closed production provenance.`); continue;
    }
    actionKeys.add(actionKey);
    if (observation.proofId === 'P-I1') {
      const witness = action.witnessId ? c.persons[action.witnessId] : undefined;
      const source = ordered.find(run => run.pcId === witness?.id && run.outcome === 'survived' && run.ordinal < r.ordinal && run.id === witness?.original?.originalRunId);
      if (action.method !== 'direct-legacy' || !witness?.original || !c.legacyIds.includes(witness.id) || !source) {
        reasons.push('P-I1 needs a direct meeting with a continuant from an earlier survived run.'); continue;
      }
    }
    verified.add(observation.proofId); sourceRuns.add(r.id);
  }
  if (PROOFS.some(id => !verified.has(id))) reasons.push('All six observations must be verified.');
  if (sourceRuns.size < 3) reasons.push('Independent verification must span three distinct closed runs.');
  return { eligible: reasons.length === 0, reasons };
}
/** Algebra only. D08 must establish these prerequisites through actual play. */
export function settle(c: Continuity, opening: SeedEnvelope, frozen: Continuity, account: ReturnType<typeof settlementAccount>, prerequisites: { crisisAnswered: boolean; approachPrepared: boolean; crossingCompleted: boolean }): Continuity {
  if (!settlementEligibility(opening, frozen).eligible) throw new Error('The Fourth Bearing is not earned at this opening');
  if (c.chronologyId !== frozen.chronologyId || c.namespace !== frozen.namespace || canonical(c.closedRuns) !== canonical(frozen.closedRuns) || canonical(c.observations) !== canonical(frozen.observations)) throw new Error('Current chronology must descend from this opening');
  if (c.settlement === 'settled' || !prerequisites.crisisAnswered || !prerequisites.approachPrepared || !prerequisites.crossingCompleted) throw new Error('Settlement still needs the answered crisis, prepared approach and completed crossing');
  assertContinuityTransition(frozen, c);
  assertCompleteAccount(c, account);
  const next = validateLedger(c);
  for (const survivor of account.living) {
    const p = next.persons[survivor.id]!;
    p.lifecycle = 'active'; p.location ??= 'Z12';
  }
  next.settlement = 'settled'; next.date = { ...FIRST_EMBER };
  assertContinuityTransition(c, next);
  return validateLedger(next);
}
